import { APIService } from "shared/services";
import { API_ENDPOINTS, API_ERROR_CODES } from "shared/constants";
import { IDataKeys, IJSONAPIItem, IAPIError } from "shared/types";
import { IScheduleBlockEntryParams, IEventBlockParams } from "Bands/types";

interface IJSONAPIScheduleBlock {
  [key: string]: IJSONAPIItem;
}

export interface IJSONAPIScheduleBlockCollection {
  [IDataKeys.eventScheduleBlock]: IJSONAPIScheduleBlock;
  meta: {
    [key: string]: IJSONAPIItem;
  };
  pagination: any;
}

export interface IJSONAPIScheduleBlockEntryCollection {
  [IDataKeys.eventScheduleTimesheetEntry]: IJSONAPIScheduleBlock;
  meta: {
    [key: string]: IJSONAPIItem;
  };
  pagination: any;
}

export class EventScheduleBlockService {
  static getBlocks = (
    queryParams?: string | { [key: string]: string | number | undefined | string[] }
  ): Promise<IJSONAPIScheduleBlockCollection> =>
    new Promise(async (resolve, reject) => {
      try {
        const blocks: unknown = await APIService.get({
          path: API_ENDPOINTS.EVENT_SCHEDULE_BLOCKS,
          data: queryParams,
        });
        resolve(blocks as IJSONAPIScheduleBlockCollection);
      } catch (error) {
        (error as IAPIError).errorCodes?.push(API_ERROR_CODES.GET_EVENT_SCHEDULE_BLOCKS);
        return reject(error);
      }
    });

  static getBlock = (id: string): Promise<IJSONAPIScheduleBlockCollection> =>
    new Promise(async (resolve, reject) => {
      try {
        const response = await APIService.get({
          path: API_ENDPOINTS.EVENT_SCHEDULE_BLOCK(id),
        });
        resolve(response);
      } catch (error) {
        (error as IAPIError).errorCodes?.push(API_ERROR_CODES.GET_EVENT_SCHEDULE_BLOCK);
        return reject(error);
      }
    });

  static createBlock = (data: IEventBlockParams): Promise<IJSONAPIScheduleBlock> =>
    new Promise(async (resolve, reject) => {
      try {
        const response = await APIService.post({
          path: API_ENDPOINTS.EVENT_SCHEDULE_BLOCKS,
          data,
        });
        resolve(response);
      } catch (error) {
        (error as IAPIError).errorCodes?.push(API_ERROR_CODES.POST_EVENT_SCHEDULE_BLOCK);
        return reject(error);
      }
    });

  static editBlockTitle = ({
    id,
    title,
  }: {
    id: string;
    title: string;
  }): Promise<IJSONAPIScheduleBlock> =>
    new Promise(async (resolve, reject) => {
      try {
        const response = await APIService.patch({
          path: API_ENDPOINTS.EVENT_SCHEDULE_BLOCK(id),
          data: { title },
        });
        resolve(response);
      } catch (error) {
        (error as IAPIError).errorCodes?.push(API_ERROR_CODES.PATCH_EVENT_SCHEDULE_BLOCK_TITLE);
        return reject(error);
      }
    });

  static editBlock = ({
    id,
    data,
  }: {
    id: string;
    data: IEventBlockParams;
  }): Promise<IJSONAPIScheduleBlock> =>
    new Promise(async (resolve, reject) => {
      try {
        const response = await APIService.patch({
          path: API_ENDPOINTS.EVENT_SCHEDULE_BLOCK(id),
          data,
        });
        resolve(response);
      } catch (error) {
        (error as IAPIError).errorCodes?.push(API_ERROR_CODES.PATCH_EVENT_SCHEDULE_BLOCK);
        return reject(error);
      }
    });

  static deleteBlock = (id: string): Promise<void> =>
    new Promise(async (resolve, reject) => {
      try {
        await APIService.delete({
          path: API_ENDPOINTS.EVENT_SCHEDULE_BLOCK(id),
        });
        resolve();
      } catch (error) {
        (error as IAPIError).errorCodes?.push(API_ERROR_CODES.DELETE_EVENT_SCHEDULE_BLOCK);
        return reject(error);
      }
    });

  static createEntry = (
    data: IScheduleBlockEntryParams
  ): Promise<IJSONAPIScheduleBlockEntryCollection> =>
    new Promise(async (resolve, reject) => {
      try {
        const response = await APIService.post({
          path: API_ENDPOINTS.EVENT_SCHEDULE_BLOCK_ENTRIES,
          data,
        });
        resolve(response);
      } catch (error) {
        (error as IAPIError).errorCodes?.push(API_ERROR_CODES.POST_EVENT_SCHEDULE_BLOCK_ENTRY);
        return reject(error);
      }
    });

  static editEntry = ({
    id,
    data,
  }: {
    id: string;
    data: IScheduleBlockEntryParams;
  }): Promise<IJSONAPIScheduleBlockEntryCollection> =>
    new Promise(async (resolve, reject) => {
      try {
        const response = await APIService.patch({
          path: API_ENDPOINTS.EVENT_SCHEDULE_BLOCK_ENTRY(id),
          data,
        });
        resolve(response);
      } catch (error) {
        (error as IAPIError).errorCodes?.push(API_ERROR_CODES.PATCH_EVENT_SCHEDULE_BLOCK_ENTRY);
        return reject(error);
      }
    });

  static deleteEntry = (id: string): Promise<void> =>
    new Promise(async (resolve, reject) => {
      try {
        await APIService.delete({
          path: API_ENDPOINTS.EVENT_SCHEDULE_BLOCK_ENTRY(id),
        });
        resolve();
      } catch (error) {
        (error as IAPIError).errorCodes?.push(API_ERROR_CODES.DELETE_EVENT_SCHEDULE_BLOCK_ENTRY);
        return reject(error);
      }
    });
}
